import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { getDefaultDashboard } from '../../../utils/roleAccess';
import { authService } from '../../../services/authService';
import { companyService } from '../../../services/companyService';
import { useLanguage } from '../../../contexts/LanguageContext';

const ForgotPasswordModal = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!email?.trim()) return;
    setStatus('sending');
    setErrorMsg('');
    const { error } = await authService?.forgotPassword(email?.trim());
    if (error) {
      setErrorMsg(error?.message || 'Failed to send reset email. Please try again.');
      setStatus('error');
    } else {
      setStatus('sent');
    }
  };

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-elevation-3 w-full max-w-md p-6 md:p-8 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-xl font-semibold text-foreground font-heading">{t('login.reset_password_title')}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth -mt-1 -mr-1 p-1 rounded-md"
            aria-label="Close"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {status === 'sent' ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <Icon name="CheckCircle2" size={20} color="#16a34a" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900 mb-1">{t('login.reset_email_sent_title')}</p>
                <p className="text-sm text-green-800 leading-relaxed">
                  {t('login.reset_email_sent_desc')} <strong>{email}</strong>. {t('login.reset_email_sent_desc2')}
                </p>
              </div>
            </div>
            <Button variant="outline" fullWidth onClick={onClose}>
              {t('close')}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('login.reset_password_desc')}
            </p>
            {status === 'error' && (
              <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <Icon name="AlertCircle" size={18} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{errorMsg}</p>
              </div>
            )}
            <Input
              type="email"
              label={t('login.email_label')}
              placeholder={t('login.email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e?.target?.value)}
              required
              disabled={status === 'sending'}
              className="w-full"
            />
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={onClose}
                disabled={status === 'sending'}
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={status === 'sending'}
                disabled={!email?.trim()}
              >
                {t('login.send_reset_link')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const { t } = useLanguage();
  // Pre-fill email when RegistrationForm's "already trialed" CTA routes the
  // user here via navigate('/login', { state: { email } }). See [B9UB0M58].
  const prefilledEmail = location?.state?.email || '';
  const [formData, setFormData] = useState({
    email: prefilledEmail,
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isUnverified, setIsUnverified] = useState(false);
  const [resendStatus, setResendStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [confirmationProcessing, setConfirmationProcessing] = useState(false);

  // Read return_to param so we can send users back to where they came from
  const returnTo = new URLSearchParams(window.location.search)?.get('return_to') || null;

  // Handle Supabase email confirmation redirect
  useEffect(() => {
    const hash = window.location?.hash;
    if (!hash) return;

    const params = new URLSearchParams(hash.replace('#', '?'));
    const accessToken = params?.get('access_token');
    const refreshToken = params?.get('refresh_token');
    const type = params?.get('type');

    if (accessToken && (type === 'signup' || type === 'recovery' || type === 'magiclink')) {
      window.history?.replaceState({}, document.title, window.location?.pathname);

      if (type === 'recovery') {
        navigate('/reset-password' + hash, { replace: true });
        return;
      }

      setConfirmationProcessing(true);
      (async () => {
        try {
          const { data, error } = await supabase?.auth?.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error || !data?.user) {
            console.error('[LoginForm] Failed to set session from confirmation token:', error);
            setErrors({ form: 'Email confirmation failed. Please try signing in directly or request a new confirmation email.' });
            setConfirmationProcessing(false);
            return;
          }

          const user = data?.user;
          const { data: profile } = await supabase?.from('user_profiles')?.select('role, company_id')?.eq('id', user?.id)?.single();

          const role = profile?.role;

          console.log('[ADMIN_LOGIN_HIT] confirmation token | user.id=', user?.id, '| role=', role);

          if (role === 'admin') {
            try {
              console.log('[ADMIN_COMPANY_LOOKUP] fetching company (confirmation token path) | user.id=', user?.id);
              const { data: company, error: companyError } = await companyService?.getUserCompany();

              console.log('[ADMIN_COMPANY_LOOKUP] result | company.id=', company?.id, '| error=', companyError?.message || null);

              if (companyError || !company) {
                console.warn('[ADMIN_REDIRECT_ERROR] company lookup failed — redirecting to setup');
                console.log('[ADMIN_REDIRECT_TARGET] → /setup-flow-plan-confirmation (fail-safe, confirmation token)');
                navigate('/setup-flow-plan-confirmation', { replace: true });
                return;
              }

              if (company?.id) {
                const subStatus = company?.subscription_status;
                const isActive = subStatus === 'active' || subStatus === 'trialing';

                if (isActive) {
                  console.log('[ADMIN_REDIRECT_TARGET] → /admin-dashboard | active subscription | company.id=', company?.id, '(confirmation token)');
                  navigate('/admin-dashboard', { replace: true });
                } else {
                  const neverPaid = !company?.stripe_subscription_id && subStatus !== 'canceled';
                  const target = neverPaid ? '/setup-flow-plan-confirmation' : '/subscription-management';
                  console.warn('[ADMIN_REDIRECT_TARGET] →', target, '| subStatus=', subStatus, '| neverPaid=', neverPaid, '(confirmation token)');
                  navigate(target, { replace: true });
                }
              } else {
                // Truly no company — brand new admin signup, send to setup flow
                console.warn('[ADMIN_ONBOARDING_BRANCH_FIRED] → /setup-flow-plan-confirmation | no company found (confirmation token)');
                navigate('/setup-flow-plan-confirmation', { replace: true });
              }
            } catch (err) {
              console.error('[ADMIN_REDIRECT_ERROR] exception (confirmation token):', err?.message);
              console.log('[ADMIN_REDIRECT_TARGET] → /setup-flow-plan-confirmation (exception fail-safe, confirmation token)');
              navigate('/setup-flow-plan-confirmation', { replace: true });
            }
          } else {
            navigate(getDefaultDashboard(role), { replace: true });
          }
        } catch (err) {
          console.error('[LoginForm] Confirmation token error:', err);
          setErrors({ form: 'Email confirmation failed. Please try signing in directly.' });
          setConfirmationProcessing(false);
        }
      })();
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = t('login.email_required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = t('login.email_invalid');
    }

    if (!formData?.password) {
      newErrors.password = t('login.password_required');
    } else if (formData?.password?.length < 6) {
      newErrors.password = t('login.password_min');
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});
    setIsUnverified(false);
    setResendStatus(null);

    console.log('[ADMIN_LOGIN_HIT] LoginForm.handleSubmit | email=', formData?.email);
    const { data, error } = await signIn(formData?.email, formData?.password);

    if (error) {
      console.error('[DEBUG][LOGIN] signInWithPassword failed. error.message=', error?.message, 'error.code=', error?.code);
      const msg = error?.message || '';
      const isEmailNotConfirmed =
        msg?.toLowerCase()?.includes('email not confirmed') ||
        msg?.toLowerCase()?.includes('not confirmed') ||
        error?.code === 'email_not_confirmed';

      if (isEmailNotConfirmed) {
        setIsUnverified(true);
      } else {
        setErrors({
          form: t('login.invalid_credentials')
        });
      }
      setIsLoading(false);
    } else {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        const { data: profile } = await supabase
          ?.from('user_profiles')
          ?.select('role, company_id')
          ?.eq('id', user?.id)
          ?.single();

        const role = profile?.role;

        console.log('[ADMIN_LOGIN_HIT] auth success | user.id=', user?.id, '| role=', role);

        if (returnTo && returnTo?.startsWith('/')) {
          navigate(returnTo, { replace: true });
          return;
        }

        if (role === 'admin') {
          try {
            console.log('[ADMIN_COMPANY_LOOKUP] fetching company for user.id=', user?.id);
            const { data: company, error: companyError } = await companyService?.getUserCompany();

            console.log('[ADMIN_COMPANY_LOOKUP] result | company.id=', company?.id, '| error=', companyError?.message || null);
            console.log('[ADMIN_SUBSCRIPTION_STATUS] subscription_status=', company?.subscription_status, '| stripe_customer_id=', company?.stripe_customer_id);
            console.log('[ADMIN_SETUP_FLAGS] setup_complete=', company?.setup_complete, '| onboarding_finished=', company?.onboarding_finished);

            if (company?.id) {
              const subStatus = company?.subscription_status;
              const isActive = subStatus === 'active' || subStatus === 'trialing';

              if (isActive) {
                console.log('[ADMIN_REDIRECT_TARGET] → /admin-dashboard | active subscription | company.id=', company?.id);
                navigate('/admin-dashboard');
              } else {
                // Inactive: never-paid → resume setup, previously-paid → billing recovery
                const neverPaid = !company?.stripe_subscription_id && subStatus !== 'canceled';
                const target = neverPaid ? '/setup-flow-plan-confirmation' : '/subscription-management';
                console.warn('[ADMIN_REDIRECT_TARGET] →', target, '| subStatus=', subStatus, '| neverPaid=', neverPaid);
                navigate(target);
              }
            } else {
              // Truly no company — brand new admin signup, send to setup flow
              console.warn('[ADMIN_ONBOARDING_BRANCH_FIRED] → /setup-flow-plan-confirmation | no company found | error=', companyError?.message || null);
              navigate('/setup-flow-plan-confirmation');
            }
          } catch (err) {
            console.error('[ADMIN_REDIRECT_ERROR] exception during company lookup:', err?.message);
            // On any exception, fail open to dashboard
            console.log('[ADMIN_REDIRECT_TARGET] → /admin-dashboard (exception fail-open)');
            navigate('/admin-dashboard');
          }
        } else {
          navigate(getDefaultDashboard(role));
        }
      } else {
        navigate('/admin-dashboard');
      }
    }
  };

  const handleResendVerification = async () => {
    setResendStatus('sending');
    const { error } = await authService?.resendVerificationEmail(formData?.email);
    if (error) {
      console.error('[LoginForm] resend verification failed:', { status: error?.status, code: error?.code, message: error?.message });
      // Distinguish rate-limit (429) from generic failure so the user sees
      // actionable copy ("wait and retry") instead of a generic error.
      const isRateLimited = error?.status === 429 ||
        error?.message?.toLowerCase()?.includes('rate limit') ||
        error?.message?.toLowerCase()?.includes('too many');
      setResendStatus(isRateLimited ? 'rate_limited' : 'error');
    } else {
      setResendStatus('sent');
    }
  };

  return (
    <>
      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}

      {/* Email confirmation processing overlay */}
      {confirmationProcessing && (
        <div className="flex flex-col items-center justify-center py-8 space-y-4">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent"></div>
          <p className="text-sm text-muted-foreground text-center">
            {t('login.confirming_email')}
          </p>
        </div>
      )}

      {!confirmationProcessing && (
      <form onSubmit={handleSubmit} className="w-full space-y-5">
        {errors?.form && (
          <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{errors?.form}</p>
          </div>
        )}

        {isUnverified && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
            <div className="flex items-start gap-3">
              <Icon name="MailWarning" size={20} color="#d97706" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900 mb-1">{t('login.email_not_verified_title')}</p>
                <p className="text-sm text-amber-800 leading-relaxed">
                  {t('login.email_not_verified_desc')}
                </p>
              </div>
            </div>
            {resendStatus === 'sent' && (
              <div className="flex items-center gap-2 text-sm text-green-700">
                <Icon name="CheckCircle2" size={16} color="#16a34a" />
                {t('login.resend_sent_msg')}
              </div>
            )}
            {resendStatus === 'rate_limited' && (
              <p className="text-sm text-destructive">Too many emails sent recently. Please wait a few minutes before trying again.</p>
            )}
            {resendStatus === 'error' && (
              <p className="text-sm text-destructive">{t('login.resend_error')}</p>
            )}
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resendStatus === 'sending' || resendStatus === 'sent'}
              className="text-sm font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
            >
              {resendStatus === 'sending' ? t('login.resend_sending') : resendStatus === 'sent' ? t('login.resend_sent') : t('login.resend_verification')}
            </button>
          </div>
        )}
        <Input
          type="email"
          name="email"
          label={t('login.email_label')}
          placeholder={t('login.email_placeholder')}
          value={formData?.email}
          onChange={handleChange}
          error={errors?.email}
          required
          disabled={isLoading}
          className="w-full"
        />
        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            label={t('login.password_label')}
            placeholder={t('login.password_placeholder')}
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            required
            disabled={isLoading}
            className="w-full"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        <div className="flex items-center justify-between gap-4">
          <Checkbox
            name="rememberMe"
            label={t('login.remember_me')}
            checked={formData?.rememberMe}
            onChange={handleChange}
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={() => setShowForgotPassword(true)}
            className="text-sm font-medium text-accent hover:text-accent/80 transition-smooth"
          >
            {t('login.forgot_password')}
          </button>
        </div>
        <Button
          type="submit"
          variant="default"
          loading={isLoading}
          fullWidth
          className="mt-6"
        >
          {t('login.sign_in_btn')}
        </Button>
      </form>
      )}
    </>
  );
};

export default LoginForm;